/**
 * Quote API Service
 * 
 * Provides standardized methods for interacting with the Quote API endpoints
 */
import api from './api';
import { useAuthStore } from '../stores/auth';

/**
 * Quote service with standardized methods for quote-related API operations
 */
export const quoteService = {
  /**
   * Get all quotes with optional filtering and pagination
   * @param {Object} params - Query parameters (page, limit, filter, etc.)
   * @returns {Promise<Object>} Paginated quotes with metadata
   */
  async getQuotes(params = {}) {
    return api.getPaginated('quotes', params);
  },

  /**
   * Get a single quote by ID with optional includes
   * @param {number|string} id - Quote ID
   * @param {Array|string} include - Related data to include
   * @returns {Promise<Object>} Quote data
   */
  async getQuote(id, includeOrParams = []) {
    let params = {};
    if (includeOrParams && typeof includeOrParams === 'object' && !Array.isArray(includeOrParams)) {
      // Object with multiple query params (e.g., { includeDetails, includeParts, include })
      params = { ...includeOrParams };
    } else if (includeOrParams) {
      params.include = includeOrParams;
    }
    return api.get(`quotes/${id}`, params);
  },

  /**
   * Create a new quote
   * @param {Object} quoteData - Quote data
   * @returns {Promise<Object>} Created quote
   */
  async createQuote(quoteData) {
    return api.post('quotes', quoteData);
  },

  /**
   * Update an existing quote
   * @param {number|string} id - Quote ID
   * @param {Object} quoteData - Updated quote data
   * @returns {Promise<Object>} Updated quote
   */
  async updateQuote(id, quoteData) {
    return api.put(`quotes/${id}`, quoteData);
  },

  /**
   * Delete a quote
   * @param {number|string} id - Quote ID
   * @returns {Promise<Object>} Deletion result
   */
  async deleteQuote(id) {
    return api.delete(`quotes/${id}`);
  },

  /**
   * Get documents for a quote
   * @param {number|string} id - Quote ID
   * @returns {Promise<Object>} Quote documents
   */
  async getQuoteDocuments(id) {
    return api.get(`quotes/${id}/documents`);
  },

  /**
   * Get document details
   * @param {number|string} quoteId - Quote ID
   * @param {number|string} documentId - Document ID
   * @returns {Promise<Object>} Document details
   */
  async getDocumentDetails(quoteId, documentId) {
    return api.get(`quotes/${quoteId}/documents/${documentId}`);
  },

  /**
   * Upload a document to a quote
   * @param {number|string} id - Quote ID
   * @param {FormData} formData - Form data with file and metadata
   * @returns {Promise<Object>} Upload result
   */
  async uploadDocument(id, formData) {
    // Create custom headers without Content-Type to let browser set it with boundary
    const headers = api.getHeaders();
    delete headers['Content-Type'];
    
    // Custom request for file upload
    try {
      const url = `${'/api'}/quotes/${id}/documents`;
      const response = await fetch(url, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: formData,
      });
      
      return api.handleResponse(response);
    } catch (error) {
      throw api.handleError(error);
    }
  },

  /**
   * Delete a document from a quote
   * @param {number|string} quoteId - Quote ID
   * @param {number|string} documentId - Document ID
   * @returns {Promise<Object>} Deletion result
   */
  async deleteDocument(quoteId, documentId) {
    return api.delete(`quotes/${quoteId}/documents/${documentId}`);
  },

  /**
   * Get items for a quote
   * @param {number|string} id - Quote ID
   * @returns {Promise<Object>} Quote items
   */
  async getQuoteItems(id) {
    return api.get(`quotes/${id}/items`);
  },

  /**
   * Add an item to a quote
   * @param {number|string} id - Quote ID
   * @param {Object} itemData - Item data
   * @returns {Promise<Object>} Added item
   */
  async addQuoteItem(id, itemData) {
    return api.post(`quotes/${id}/items`, itemData);
  },

  /**
   * Update a quote item
   * @param {number|string} quoteId - Quote ID
   * @param {number|string} itemId - Item ID
   * @param {Object} itemData - Updated item data
   * @returns {Promise<Object>} Updated item
   */
  async updateQuoteItem(quoteId, itemId, itemData) {
    return api.put(`quotes/${quoteId}/items/${itemId}`, itemData);
  },

  /**
   * Delete a quote item
   * @param {number|string} quoteId - Quote ID
   * @param {number|string} itemId - Item ID
   * @returns {Promise<Object>} Deletion result
   */
  async deleteQuoteItem(quoteId, itemId) {
    return api.delete(`quotes/${quoteId}/items/${itemId}`);
  },

  /**
   * Get labor items for a quote
   * @param {number|string} id - Quote ID
   * @returns {Promise<Array>} Labor items
   */
  async getQuoteLaborItems(id) {
    return api.get(`quotes/${id}/labor-items`);
  },

  /**
   * Process a supplier quote document with automatic parsing
   * @param {number|string} quoteId - Quote ID
   * @param {Object} data - Processing data (document_id, parser_type)
   * @returns {Promise<Object>} Processing result with items_parsed count
   */
  async processSupplierQuote(quoteId, data) {
    return api.post(`quotes/${quoteId}/upload-supplier-quote`, data);
  },

  /**
   * Export quote items to CSV
   * @param {number|string} quoteId - Quote ID
   * @returns {string} CSV download URL with authentication token
   */
  getExportCsvUrl(quoteId) {
    // For file downloads we need to use token as query param
    const authStore = useAuthStore();
    const token = authStore.token;
    return `/api/quotes/${quoteId}/export-csv?token=${token}`;
  },

  /**
   * Get document download URL
   * @param {number|string} quoteId - Quote ID
   * @param {number|string} documentId - Document ID
   * @returns {string} Document download URL with authentication token
   */
  getDocumentDownloadUrl(quoteId, documentId, disposition = 'inline') {
    // For file downloads we need to use token as query param
    // disposition='inline' for iframe display, 'attachment' for download
    const authStore = useAuthStore();
    const token = authStore.token;
    return `/api/quotes/${quoteId}/documents/${documentId}/download?token=${token}&disposition=${disposition}`;
  },

  /**
   * Add manual supplier parts to a quote
   * @param {number|string} quoteId - Quote ID
   * @param {Object} partsData - Manual parts data
   * @returns {Promise<Object>} API response
   */
  async addManualSupplierParts(quoteId, partsData) {
    // Import logger dynamically to avoid circular dependencies
    const { manualPartsLogger } = await import('../utils/manualPartsLogger.js');
    const logger = manualPartsLogger;

    logger.info('quoteService', 'addManualSupplierParts_start', {
      quoteId,
      partsCount: partsData.parts?.length,
      documentId: partsData.document_id,
      totalPrice: partsData.total_price,
      useManualTotal: partsData.use_manual_total,
    });

    try {
      // Validate input data
      if (!quoteId) {
        throw new Error('Quote ID is required');
      }
      if (!partsData.document_id) {
        throw new Error('Document ID is required');
      }
      if (!partsData.parts || !Array.isArray(partsData.parts)) {
        throw new Error('Parts array is required');
      }
      if (partsData.parts.length === 0) {
        throw new Error('At least one part is required');
      }

      logger.debug('quoteService', 'addManualSupplierParts_validation_passed', {
        quoteId,
        validationChecks: {
          quoteId: !!quoteId,
          documentId: !!partsData.document_id,
          partsArray: Array.isArray(partsData.parts),
          partsCount: partsData.parts.length,
        },
      });

      // Start performance timer
      const endTimer = logger.startTimer('quoteService', 'addManualSupplierParts_api_call');

      const result = await api.post(`quotes/${quoteId}/manual-supplier-parts`, partsData);

      // End performance timer
      const duration = endTimer();

      logger.info('quoteService', 'addManualSupplierParts_success', {
        quoteId,
        partsAdded: result.parts_added,
        totalPrice: result.total_price,
        duration_ms: duration,
        responseSize: JSON.stringify(result).length,
      });

      return result;

    } catch (error) {
      logger.error('quoteService', 'addManualSupplierParts_failed', error, {
        quoteId,
        partsDataSummary: {
          document_id: partsData.document_id,
          parts_count: partsData.parts?.length,
          total_price: partsData.total_price,
          use_manual_total: partsData.use_manual_total,
        },
        errorType: error.constructor.name,
      });
      throw error;
    }
  },

  /**
   * Create equipment in quote context
   * @param {number|string} quoteId - Quote ID
   * @param {Object} equipmentData - Equipment data (template_id, name, space_id, etc.)
   * @returns {Promise<Object>} Created equipment
   */
  async createQuoteEquipment(quoteId, equipmentData) {
    return api.post(`quotes/${quoteId}/equipment`, equipmentData);
  },

  /**
   * Get all equipment for a quote
   * @param {number|string} quoteId - Quote ID
   * @returns {Promise<Array>} Quote equipment
   */
  async getQuoteEquipment(quoteId) {
    return api.get(`quotes/${quoteId}/equipment`);
  },

  /**
   * Mark equipment as replacement
   * @param {number|string} quoteId - Quote ID
   * @param {number|string} equipmentId - Equipment ID
   * @param {Object} data - Replacement data (replaces_equipment_id)
   * @returns {Promise<Object>} Updated equipment
   */
  async markEquipmentAsReplacement(quoteId, equipmentId, data) {
    return api.post(`quotes/${quoteId}/equipment/${equipmentId}/mark-as-replacement`, data);
  },

  /**
   * Get BOM reconciliation data for a quote
   * @param {number|string} quoteId - Quote ID
   * @returns {Promise<Object>} BOM reconciliation data with matched/shortage/excess parts
   */
  async getBomReconciliation(quoteId) {
    return api.get(`quotes/${quoteId}/bom-reconciliation`);
  },

  /**
   * Create a new version of a quote
   * @param {number|string} quoteId - Quote ID
   * @param {Object} options - Version options
   * @param {boolean} [options.copyItems] - Copy items to new version
   * @param {boolean} [options.copyLaborItems] - Copy labor items to new version
   * @returns {Promise<Object>} Created quote version
   */
  async createQuoteVersion(quoteId, options = {}) {
    return api.post(`quotes/${quoteId}/new-version`, {
      copy_items: options.copyItems !== false,
      copy_labor_items: options.copyLaborItems !== false,
    });
  },

  /**
   * Create a quote for a tender
   * @param {number|string} tenderId - Tender ID
   * @param {Object} quoteData - Quote data
   * @returns {Promise<Object>} Created quote
   */
  async createQuoteForTender(tenderId, quoteData) {
    return api.post(`tenders/${tenderId}/quotes`, {
      ...quoteData,
      tender_id: tenderId,
    });
  },

  /**
   * Apply default markup to all quote items
   * @param {number|string} quoteId - Quote ID
   * @param {Object} data - Markup data
   * @param {number} [data.markup] - Markup percentage to apply (optional, uses quote default if not provided)
   * @returns {Promise<Object>} Result with items_updated count
   */
  async applyDefaultMarkup(quoteId, data = {}) {
    return api.post(`quotes/${quoteId}/apply-default-markup`, data);
  },

  /**
   * Get financial summary for a quote
   * @param {number|string} quoteId - Quote ID
   * @returns {Promise<Object>} Financial summary with totals, profit, margins
   */
  async getFinancialSummary(quoteId) {
    return api.get(`quotes/${quoteId}/financial-summary`);
  },
};

export default quoteService;